import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, ConciergeBell, FileText, HelpCircle, Mail, ShieldCheck } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SeoHead from '../components/seo/SeoHead';
import { publicService } from '../services/public.service';
import { showAppAlert } from '../utils/appAlert';

const iconMap = {
  careers: Briefcase,
  services: ConciergeBell,
  contact: Mail,
  faqs: HelpCircle,
  privacy: ShieldCheck,
  terms: FileText
};

const pageContent = {
  careers: {
    canonicalPath: '/careers',
    eyebrow: 'Join Go Rides',
    title: 'Careers at Go Rides',
    description: 'We are building a trusted, modern mobility platform for Rwanda. Explore how you can grow with a team focused on elegant service, strong operations, and real customer impact.',
    sections: [
      {
        title: 'What we look for',
        body: 'We value ownership, customer empathy, operational discipline, and people who can turn good ideas into reliable execution.'
      },
      {
        title: 'Current focus areas',
        items: ['Customer support and experience', 'Fleet operations and quality control', 'Partnership growth', 'Engineering and product delivery']
      }
    ],
    ctaLabel: 'Contact Our Team',
    ctaTo: '/contact'
  },
  services: {
    canonicalPath: '/services',
    eyebrow: 'What We Offer',
    title: 'Go Rides Services',
    description: 'Go Rides connects renters and fleet owners through a premium booking experience with transparent pricing, trusted listings, and operational support built for Rwanda.',
    sections: [
      {
        title: 'For renters',
        items: ['Browse verified vehicles', 'Flexible booking durations', 'Transparent pricing breakdowns', 'Support before and during your trip']
      },
      {
        title: 'For car owners',
        items: ['Fleet listing and approval workflow', 'Availability and pricing management', 'Booking visibility and earnings tracking', 'Admin and support assistance']
      }
    ],
    ctaLabel: 'Explore Fleet',
    ctaTo: '/browse'
  },
  contact: {
    canonicalPath: '/contact',
    eyebrow: 'Talk To Us',
    title: 'Contact Go Rides',
    description: 'Need help with a booking, fleet listing, partnership, or support issue? Send us a message and our team will get back to you.',
    sections: [
      {
        title: 'Support channels',
        items: ['General inquiries and partnerships', 'Booking support and renter assistance', 'Fleet onboarding and owner support']
      }
    ],
    ctaLabel: 'Browse Fleet',
    ctaTo: '/browse'
  },
  faqs: {
    canonicalPath: '/faqs',
    eyebrow: 'Helpful Answers',
    title: 'Frequently Asked Questions',
    description: 'Quick answers to common questions about bookings, payments, fleet listings, and support on Go Rides.',
    sections: [
      {
        title: 'Booking',
        items: ['You can browse available cars by city, category, fuel type, seats, and price range.', 'Same-day pickup and return is treated as a one-day booking.', 'Reservations can be created first and paid within the allowed payment window.']
      },
      {
        title: 'Fleet owners',
        items: ['Owners can submit vehicles for approval from their dashboard.', 'Rejected cars can be updated and resubmitted.', 'Approved vehicles can be managed directly from the owner portal.']
      }
    ],
    ctaLabel: 'Need More Help?',
    ctaTo: '/contact'
  },
  privacy: {
    canonicalPath: '/privacy-policy',
    eyebrow: 'Privacy',
    title: 'Privacy Policy',
    description: 'This page explains how Go Rides collects, uses, and protects customer, owner, and booking information while operating the platform.',
    sections: [
      {
        title: 'Information we collect',
        items: ['Account details such as name, email, phone number, and verification information', 'Booking and payment-related data', 'Uploaded documents and platform activity required for operations and compliance']
      },
      {
        title: 'How we use it',
        items: ['To process registrations, bookings, and verification', 'To improve safety, support, and operational quality', 'To send important account, booking, and platform notifications']
      }
    ],
    ctaLabel: 'View Terms',
    ctaTo: '/terms-of-service'
  },
  terms: {
    canonicalPath: '/terms-of-service',
    eyebrow: 'Legal',
    title: 'Terms of Service',
    description: 'These terms describe the responsibilities of renters, car owners, and platform administrators when using Go Rides.',
    sections: [
      {
        title: 'Platform use',
        items: ['Users must provide accurate information when creating and maintaining accounts.', 'Bookings, listings, and reviews must reflect genuine usage of the platform.', 'Go Rides may suspend or restrict accounts that misuse the service or violate policy.']
      },
      {
        title: 'Bookings and listings',
        items: ['Vehicle availability, approval, and booking status are controlled by platform rules and operational review.', 'Payments, cancellations, and time-limited reservations are governed by the applicable booking flow.', 'Owners are responsible for keeping vehicle details and documentation accurate.']
      }
    ],
    ctaLabel: 'Contact Support',
    ctaTo: '/contact'
  }
};

const PublicInfoPage = ({ pageKey }) => {
  const page = useMemo(() => pageContent[pageKey], [pageKey]);
  const Icon = iconMap[pageKey] || FileText;
  const [submitting, setSubmitting] = useState(false);
  const [contactForm, setContactForm] = useState({
    fullName: '',
    email: '',
    subject: '',
    message: ''
  });

  if (!page) {
    return null;
  }

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    if (submitting) {
      return;
    }

    try {
      setSubmitting(true);
      await publicService.submitContactMessage(contactForm);
      setContactForm({
        fullName: '',
        email: '',
        subject: '',
        message: ''
      });
      showAppAlert({
        type: 'success',
        title: 'Message sent',
        message: 'Your message has been sent successfully. Our team will get back to you soon.'
      });
    } catch (error) {
      showAppAlert({
        type: 'error',
        title: 'Message failed',
        message: error.message || 'We could not send your message right now.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] text-brand-black flex flex-col">
      <SeoHead
        title={`${page.title} | Go Rides`}
        description={page.description}
        canonicalPath={page.canonicalPath}
      />
      <Navbar />

      <main className="flex-1">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20">
          <div className="rounded-[2rem] border border-gray-100 bg-white p-8 lg:p-12 shadow-sm">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-black text-white mb-6">
              <Icon className="w-6 h-6" />
            </div>
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-gray-400 mb-4">{page.eyebrow}</p>
            <h1 className="text-4xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-6">{page.title}</h1>
            <p className="max-w-3xl text-base lg:text-lg leading-8 text-gray-500">{page.description}</p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-8">
            <div className="space-y-6">
              {page.sections.map((section) => (
                <div key={section.title} className="rounded-[1.75rem] border border-gray-100 bg-white p-7 lg:p-8 shadow-sm">
                  <h2 className="text-2xl font-black tracking-tight mb-4">{section.title}</h2>
                  {section.body ? (
                    <p className="text-sm sm:text-base leading-8 text-gray-500">{section.body}</p>
                  ) : null}
                  {section.items ? (
                    <div className="space-y-3">
                      {section.items.map((item) => (
                        <div key={item} className="flex items-start gap-3 text-sm sm:text-base text-gray-600 leading-7">
                          <span className="mt-2 h-2 w-2 rounded-full bg-brand-black flex-shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="space-y-6">
              {pageKey === 'contact' ? (
                <div className="rounded-[1.75rem] border border-gray-100 bg-white p-7 lg:p-8 shadow-sm">
                  <h2 className="text-2xl font-black tracking-tight mb-6">Send us a message</h2>
                  <form className="space-y-4" onSubmit={handleContactSubmit}>
                    <input
                      type="text"
                      placeholder="Full name"
                      value={contactForm.fullName}
                      onChange={(event) => setContactForm((current) => ({ ...current, fullName: event.target.value }))}
                      className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-sm outline-none focus:border-brand-black focus:bg-white"
                      required
                    />
                    <input
                      type="email"
                      placeholder="Email address"
                      value={contactForm.email}
                      onChange={(event) => setContactForm((current) => ({ ...current, email: event.target.value }))}
                      className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-sm outline-none focus:border-brand-black focus:bg-white"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Subject"
                      value={contactForm.subject}
                      onChange={(event) => setContactForm((current) => ({ ...current, subject: event.target.value }))}
                      className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-sm outline-none focus:border-brand-black focus:bg-white"
                      required
                    />
                    <textarea
                      placeholder="How can we help?"
                      value={contactForm.message}
                      onChange={(event) => setContactForm((current) => ({ ...current, message: event.target.value }))}
                      rows={6}
                      className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-sm outline-none focus:border-brand-black focus:bg-white"
                      required
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center justify-center rounded-xl bg-brand-black px-6 py-4 text-[11px] font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-black disabled:opacity-60"
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="rounded-[1.75rem] border border-gray-100 bg-white p-7 lg:p-8 shadow-sm">
                  <p className="text-[10px] font-black uppercase tracking-[0.22em] text-gray-400 mb-4">Next step</p>
                  <h2 className="text-2xl font-black tracking-tight mb-4">Keep exploring Go Rides</h2>
                  <p className="text-sm sm:text-base leading-8 text-gray-500 mb-6">
                    Browse our fleet, create an account, or reach out to our team if you need help with rentals, fleet listing, or partnerships.
                  </p>
                  <Link
                    to={page.ctaTo}
                    className="inline-flex items-center gap-2 rounded-xl bg-brand-black px-6 py-4 text-[11px] font-black uppercase tracking-[0.18em] text-white hover:bg-black transition-colors"
                  >
                    {page.ctaLabel}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PublicInfoPage;
